import React, {
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  ListItem,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import {
  Contact,
  Address as SubscriptionAddress,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/billing_pb";
import { Tier } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/common_pb";
import { SubscriptionComponent } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/components_pb";
import {
  BillingFrequency,
  ContractStatus,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";
import { Coupon } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/coupons_pb";
import {
  CreditCardDetails,
  PayPalAccountDetails,
  PaymentCollectionMethod,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/payment_method_pb";
import {
  SubscriptionData,
  SubscriptionState,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/subscriptions_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import ConfirmDialog from "../../../shared/confirm-dialog";
import SubscriptionAction from "./subscription-action";
import { getSubscriptionComponentBySubId } from "api/endpoints/components";
import {
  ReactivateSubscriptionParams,
  reactivateSubscription as reactivateSubscriptionApi,
  validateCoupon as validateCouponApi,
} from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import AddressForm from "components/shared/address/address-form";
import { contractBillingFreqToString } from "constants/contracts";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { CardExpirationStatus } from "constants/payment-method";
import {
  ContractsListContext,
  OrgDataContext,
} from "context/organisation-context";
import { PaymentMethodsContext } from "context/payment-methods-context";
import { PermissionsContext } from "context/permissions-context";
import { SnackbarContext } from "context/snackbar-context";
import { SubscriptionActionsContext } from "context/subscriptions-action-context";
import {
  ENTRY_PERISCOPE,
  generateContractMetadata,
} from "helpers/contract-metadata";
import { useAddress } from "helpers/hooks/use-address";
import useDebouncedEffect from "helpers/hooks/use-debounced-effect";
import { toUUID } from "helpers/s12-helpers";
import {
  getCardExpirationStatus,
  getCouponProvider,
} from "helpers/subscriptions";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { upCaseString } from "helpers/utils";
import { Validator } from "helpers/validator";
import { Address } from "types/address";
import { UrlParamTypes } from "types/org-display";

interface ReactivateSubscriptionZuoraProps {
  refreshOrgData: () => void;
  subscription: SubscriptionData;
}

const subscriptionTierDisplayMapping: Record<number, string> = {
  [Tier.PREMIUM]: "Premium",
  [Tier.ENTERPRISE]: "Enterprise",
  [Tier.FREE]: "Free",
  [Tier.UNSPECIFIED]: "Unspecified",
};

const ReactivateSubscriptionZuora: React.FC<
  ReactivateSubscriptionZuoraProps
> = ({ refreshOrgData, subscription }: ReactivateSubscriptionZuoraProps) => {
  const theme = useTheme();
  const { orgData } = useContext(OrgDataContext);
  const {
    defaultPaymentMethod,
    defaultPaymentMethodDetails,
    setDefaultPaymentMethodDetails,
    setDefaultPaymentMethod,
  } = useContext(PaymentMethodsContext);
  const { showError } = useContext(SnackbarContext);
  const { isSubscriptionActionLoading, setIsSubscriptionActionLoading } =
    useContext(SubscriptionActionsContext);
  const { hasPermissions } = useContext(PermissionsContext);

  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [showLoadingSyncSpinner, setShowLoadingSyncSpinner] = useState(false);
  const [isDialogueOpen, setisDialogueOpen] = useState(false);
  const [isSameShippingContact, setIsSameShippingContact] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [contactEmail, setContactEmail] = useState(
    orgData?.info?.email ? orgData?.info?.email : "",
  );
  const [contactNumber, setContactNumber] = useState(
    orgData?.info?.phone ? orgData?.info?.phone : "",
  );
  const [shippingContact, setShippingContact] = useState({
    firstName: "",
    lastName: "",
    email: orgData?.info?.email ? orgData?.info?.email : "",
    phoneNumber: orgData?.info?.phone ? orgData?.info?.phone : "",
  });
  const [couponCode, setCouponCode] = useState("");
  const [billingAddress, setBillingAddress] = useAddress({
    address: "",
    postcode: "",
    city: "",
    state: "",
    country: "",
    address2: "",
  });
  const [shippingAddress, setShippingAddress] = useAddress({
    address: "",
    postcode: "",
    city: "",
    state: "",
    country: "",
    address2: "",
  });
  const [useShippingAsBilling, setUseShippingAsBilling] = useState(true);

  const [includeTrial, setIncludeTrial] = useState(false);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [componentMsg, setComponentMsg] = useState("");
  const [couponDescription, setCouponDescription] = useState<Coupon>();
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const { contractsList, getCurrentContract } =
    useContext(ContractsListContext);

  // Default existing values from current contract/plan/subscription
  const currentContract = getCurrentContract(contractsList);
  const planBillingFrequency =
    currentContract?.plans?.[0]?.billingFrequency ||
    BillingFrequency.BILLING_FREQUENCY_UNSPECIFIED;

  const [updatedPlanType, setUpdatedPlanType] = useState(
    [
      BillingFrequency.BILLING_FREQUENCY_ANNUAL,
      BillingFrequency.BILLING_FREQUENCY_MONTHLY,
    ].includes(planBillingFrequency)
      ? planBillingFrequency
      : BillingFrequency.BILLING_FREQUENCY_ANNUAL,
  );

  const canApplyCoupon = hasPermissions([
    ActionType.APPLY_COUPON,
    ActionType.VALIDATE_COUPON,
  ]);
  const billingProviderName = "Zuora";
  const { validateEmail } = new Validator();

  const paymentCollectionMethod =
    subscription.paymentCollectionMethod === PaymentCollectionMethod.AUTOMATIC;
  const isAutomaticWithoutPaymentMethod =
    paymentCollectionMethod && !defaultPaymentMethod;

  const hasExpiredCardAttached =
    defaultPaymentMethodDetails?.getType() === CreditCardDetails &&
    getCardExpirationStatus(
      defaultPaymentMethodDetails as CreditCardDetails,
    ) === CardExpirationStatus.EXPIRED;

  const hasProviderSubscriptionId =
    (subscription.providerSubscriptionId &&
      subscription.providerSubscriptionId.length > 0) ||
    false;

  const isDecoupledEndOfTrial =
    !subscription.active &&
    !hasProviderSubscriptionId &&
    subscription.state === SubscriptionState.CANCELED;

  const isSubmitDisabled =
    (isApplyingCoupon && !couponCode) ||
    !contactEmail ||
    !firstName ||
    !lastName ||
    !shippingAddress.country ||
    (shippingAddress.country === "US" && !shippingAddress.postcode) ||
    (isAutomaticWithoutPaymentMethod && !includeTrial) ||
    (hasExpiredCardAttached && !includeTrial);

  useEffect(() => {
    setShippingAddress({
      ...shippingAddress,
      address: orgData?.info?.address?.street || "",
      postcode: orgData?.info?.address?.postalCode || "",
      city: orgData?.info?.address?.city || "",
      state: orgData.info?.address?.region || "",
      address2: orgData?.info?.address?.streetCont || "",
      country: orgData?.info?.address?.countryCode || "",
    });

    getSubscriptionComponent();
    // eslint-disable-next-line
  }, [orgData, isDialogueOpen]);

  // Sync shipping contact with billing contact when checkbox is checked
  useEffect(() => {
    if (isSameShippingContact) {
      setShippingContact({
        firstName: firstName || "",
        lastName: lastName || "",
        email: contactEmail || "",
        phoneNumber: contactNumber || "",
      });
    }
  }, [firstName, lastName, contactEmail, contactNumber, isSameShippingContact]);

  // Sync billing address with shipping address when checkbox is checked
  useEffect(() => {
    if (useShippingAsBilling && shippingAddress.country) {
      setBillingAddress({
        address: shippingAddress.address || "",
        postcode: shippingAddress.postcode || "",
        city: shippingAddress.city || "",
        state: shippingAddress.state || "",
        country: shippingAddress.country || "",
        address2: shippingAddress.address2 || "",
      });
    }
  }, [shippingAddress, useShippingAsBilling]);

  const onBillingAddressChange = (updatedbillingAddress: Address) => {
    setBillingAddress(updatedbillingAddress);
  };

  const onShippingAddressChange = (updatedShippingAddress: Address) => {
    setShippingAddress(updatedShippingAddress);
  };

  const handleShippingContactChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: keyof typeof shippingContact,
  ) => {
    setShippingContact({
      ...shippingContact,
      [key]: event.target.value,
    });
  };

  const onSameShippingContactChange = (value: boolean) => {
    setIsSameShippingContact(value);

    if (value) {
      // If checking the box, copy billing contact to shipping
      setShippingContact({
        firstName: firstName || "",
        lastName: lastName || "",
        email: contactEmail || "",
        phoneNumber: contactNumber || "",
      });
    }
    // If unchecking, leave shipping contact as is so user can edit independently
  };

  const handleApplyCoupon = (isApplyingCoupon: boolean) => {
    if (!isApplyingCoupon) {
      setCouponCode("");
    }
    setIsApplyingCoupon(isApplyingCoupon);
  };

  const isReactivateBtnDisabled = () => {
    const isActive = subscription?.active;
    const isCanceled = subscription?.state === SubscriptionState.CANCELED;
    const isFree = subscription?.tier === Tier.FREE;

    return isSubscriptionActionLoading || isActive || !isCanceled || isFree;
  };

  useDebouncedEffect(
    () => {
      validateCoupon(couponCode);
    },
    1000,
    [couponCode],
  );

  const validateCoupon = (code: string) => {
    if (code.length > 0) {
      validateCouponApi(
        {
          code,
          provider: getCouponProvider(orgData.subscription[0]),
        },
        callOptionsHeaders(regionCode, orgData.id),
      )
        .then((res) => {
          setCouponCode(res.coupon?.code || "");
          setCouponDescription(res.coupon);
        })
        .catch(() => {
          setCouponCode("");
        });
    }
  };

  const formatSubscriptionComponentMsg = (
    subscriptionComponents: SubscriptionComponent[],
  ) => {
    if (subscriptionComponents && subscriptionComponents.length > 0) {
      const price = (
        subscriptionComponents[0].componentPrice[0].price / 100
      ).toFixed(2);
      const currency = subscriptionComponents[0].componentPrice[0].currency;
      return `$${price} ${currency} per seat.`;
    }

    return (
      "Subscription components not found. Check " +
      billingProviderName +
      " to verify attached components."
    );
  };

  const getSubscriptionComponent = useCallback(() => {
    setComponentMsg("Loading subscription components...");
    getSubscriptionComponentBySubId(
      {
        subId: toUUID(subscription.subscriptionId),
      },
      callOptionsHeaders(regionCode, roleId),
    )
      .then((res) => {
        setComponentMsg(
          formatSubscriptionComponentMsg(res?.subscriptionComponents),
        );
      })
      .catch((e) => {
        showError(e);
        setComponentMsg(
          "Error loading subscription components. Check " +
            billingProviderName +
            " to verify attached components.",
        );
      });
  }, []);

  const reactivateSubscription = () => {
    setShowLoadingSpinner(true);
    setIsSubscriptionActionLoading(true);

    const billingAddr = new SubscriptionAddress({
      thoroughfare: billingAddress.address,
      locality: billingAddress.city,
      postalCode: billingAddress.postcode,
      administrativeArea: billingAddress.state,
      country: billingAddress.country,
    });
    if (billingAddress.address2) {
      billingAddr.subThoroughfare = billingAddress.address2;
    }

    const shippingAddr = new SubscriptionAddress({
      thoroughfare: shippingAddress.address,
      locality: shippingAddress.city,
      postalCode: shippingAddress.postcode,
      administrativeArea: shippingAddress.state,
      country: shippingAddress.country,
    });
    if (shippingAddress.address2) {
      shippingAddr.subThoroughfare = shippingAddress.address2;
    }

    const metadata = generateContractMetadata({
      status: includeTrial ? ContractStatus.TRIALING : ContractStatus.ACTIVATED,
      entry: ENTRY_PERISCOPE,
    });

    const billingContact = new Contact({
      firstName: firstName,
      lastName: lastName,
      email: contactEmail,
      phoneNumber: contactNumber,
    });

    const shippingContactObj = new Contact({
      firstName: shippingContact.firstName,
      lastName: shippingContact.lastName,
      email: shippingContact.email,
      phoneNumber: shippingContact.phoneNumber,
    });

    const reactivateReq: ReactivateSubscriptionParams = {
      orgId: roleId,
      userId: orgData.members?.[0],
      billingContact,
      shippingContact: shippingContactObj,
      couponCode,
      includeTrial,
      billingAddress: billingAddr,
      shippingAddress: shippingAddr,
      metadata: includeTrial ? metadata : undefined, // only add the metadata if going for trialing for now
      // Deprecated fields - set for backwards compatibility
      contactEmail: contactEmail,
      contactNumber: contactNumber,
    };

    if (isDecoupledEndOfTrial) {
      reactivateReq.billingFrequency = updatedPlanType;
    }

    reactivateSubscriptionApi(
      reactivateReq,
      callOptionsHeaders(regionCode, roleId),
    )
      .then(() => {
        setOpenSuccessAlert(true);
        refreshOrgData();
      })
      .catch(showError)
      .finally(() => {
        setShowLoadingSpinner(false);
        setIsSubscriptionActionLoading(false);
      });
  };

  const cancelChanges = () => {
    setisDialogueOpen(false);
    setFirstName("");
    setLastName("");
    setShippingContact({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
    });
    setCouponCode("");
    setBillingAddress({});
    setShippingAddress({});
    setUseShippingAsBilling(true);
    setIsApplyingCoupon(false);
    setCouponDescription(undefined);
    setIncludeTrial(false);
  };

  const handleSnackbarClose = (
    event?: Event | SyntheticEvent<unknown, Event>,
    reason?: string | undefined,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
  };

  const onSameBillingChange = (value: boolean) => {
    setUseShippingAsBilling(value);

    setBillingAddress({
      ...shippingAddress,
      address: shippingAddress.address || "",
      postcode: shippingAddress.postcode || "",
      city: shippingAddress.city || "",
      state: shippingAddress.state || "",
      country: shippingAddress.country || "",
      address2: shippingAddress.address2 || "",
    });
  };

  const displayBillingInfoMessage = () => {
    if (subscription.currency.toUpperCase() === "EUR") {
      return "This customer will be billed in EUR";
    }

    if (subscription.currency.toUpperCase() === "GBP") {
      return "This customer will be billed in GBP";
    }

    let countryInfoMessage: string;
    switch (shippingAddress.country) {
      case "AU":
        countryInfoMessage = "AUD and charged GST";
        break;
      case "NZ":
        countryInfoMessage = "AUD";
        break;
      case "US":
        countryInfoMessage = "USD and charged Sales Tax";
        break;
      default:
        countryInfoMessage = "USD";
    }

    return `This customer will be billed in ${countryInfoMessage}`;
  };

  const infoContainerSx = {
    background: "#fff5d4",
    border: "1px solid #94671c",
    color: "#94671c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "6px 4px 0px",
  };

  const displayCouponDescription = () => {
    if (couponDescription && couponDescription.discountPercentage) {
      const discountPercentage =
        couponDescription.discountPercentage.toPrecision(4);
      const endDateMsg = couponDescription.endDate
        ? `will expire on ${formatDateTimeDisplay(
            convertTimestampToDate(couponDescription.endDate),
          )}`
        : "does not expire";
      return `${discountPercentage}% discount will be applied and ${endDateMsg}.`;
    }
  };

  const displayPaymentMethodDescription = () => {
    if (defaultPaymentMethodDetails?.getType() === CreditCardDetails) {
      return `Charged against card ending in
        ${(defaultPaymentMethodDetails as CreditCardDetails).lastFour}
      `;
    }
    if (defaultPaymentMethodDetails?.getType() === PayPalAccountDetails) {
      return `Charged against PayPal account -
        ${(defaultPaymentMethodDetails as PayPalAccountDetails).email}
      `;
    }
  };

  const infoPanel = () => {
    const includeTrialMsg = includeTrial
      ? "at the end of the trial period"
      : "immediately";
    return (
      <Box width={1} sx={infoContainerSx}>
        <ListItem
          sx={{ display: "list-item", padding: "0px 0px" }}
          data-testId={"reactivate-plan-sub-component-msg"}
        >
          <b>Current component price point:</b> {componentMsg}
        </ListItem>

        {shippingAddress.country && (
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            {`${displayBillingInfoMessage()} ${includeTrialMsg}.`}
          </ListItem>
        )}

        {isApplyingCoupon && couponCode && couponDescription && (
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            {displayCouponDescription()}
          </ListItem>
        )}

        {defaultPaymentMethod && !hasExpiredCardAttached && (
          <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
            {displayPaymentMethodDescription()}
          </ListItem>
        )}
      </Box>
    );
  };

  const displayAlertBanner = () => {
    if (isAutomaticWithoutPaymentMethod) {
      return (
        <Grid
          item
          xs={12}
          style={{ marginTop: "15px", marginBottom: "10px" }}
          data-testid="automatic-no-payment-profile"
        >
          <Box width={1} sx={warnContainerSx}>
            {isAutomaticWithoutPaymentMethod && (
              <>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  <b>
                    This customer is on automatic collection without a payment
                    method.
                  </b>
                </ListItem>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  Please send the customer the {billingProviderName}{" "}
                  self-service form to update their payment method before
                  reactivating.
                </ListItem>
              </>
            )}
            {includeTrial && (
              <ListItem
                sx={{ display: "list-item", padding: "0px 0px" }}
                data-testid="automatic-trial-no-payment-profile-trial"
              >
                If the customer does not update their payment profile by the end
                of the trial period they will transition to <b>past due</b>.
              </ListItem>
            )}
            {isDecoupledEndOfTrial && (
              <>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  <Typography component={"span"}>
                    This organization has completed their 30-day decoupled
                    trial. This means that the trial subscription{" "}
                    <b>does not exist</b> in {billingProviderName}.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  <Typography component={"span"}>
                    Completing this form will reactivate their plan back onto a
                    Premium Trial and create the {billingProviderName}{" "}
                    subscription.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  <Typography component={"span"}>
                    This will unlock other subscription-related Periscope
                    functionality.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  <Typography component={"span"}>
                    Please contact #business-systems for any enquiries.
                  </Typography>
                </ListItem>
              </>
            )}
          </Box>
        </Grid>
      );
    }

    if (defaultPaymentMethodDetails?.getType() === CreditCardDetails) {
      const card = defaultPaymentMethodDetails as CreditCardDetails;
      const { expirationMonth, expirationYear } = card;
      const cardStatus = getCardExpirationStatus(card);
      const formattedExpirationMonth = expirationMonth
        .toString()
        .padStart(2, "0");

      if (
        [CardExpirationStatus.EXPIRING, CardExpirationStatus.EXPIRED].includes(
          cardStatus,
        )
      ) {
        return (
          <>
            <Grid
              item
              xs={12}
              style={{ marginTop: "15px", marginBottom: "10px" }}
            >
              <Box width={1} sx={warnContainerSx}>
                <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                  This customer&lsquo;s credit card{" "}
                  {cardStatus === CardExpirationStatus.EXPIRED
                    ? "has expired on"
                    : "is expiring soon on"}{" "}
                  <b>
                    {formattedExpirationMonth}/{expirationYear}
                  </b>
                </ListItem>
                {cardStatus === CardExpirationStatus.EXPIRING && (
                  <>
                    <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                      Please send the customer the {billingProviderName}{" "}
                      self-service form to update their payment method to
                      prevent transitioning to past due at the next billing
                      cycle.
                    </ListItem>
                  </>
                )}
                {cardStatus === CardExpirationStatus.EXPIRED && (
                  <>
                    <ListItem sx={{ display: "list-item", padding: "0px 0px" }}>
                      Please send the customer the {billingProviderName}{" "}
                      self-service form to update their payment method before
                      reactivating.
                    </ListItem>
                  </>
                )}
              </Box>
            </Grid>
          </>
        );
      }
    }
    return;
  };

  const displayBillingFrequencyOptions = () => {
    return [
      BillingFrequency.BILLING_FREQUENCY_MONTHLY,
      BillingFrequency.BILLING_FREQUENCY_ANNUAL,
    ].map((freq) => {
      return (
        <MenuItem
          key={`billing-frequency-${contractBillingFreqToString[
            freq
          ].toLowerCase()}`}
          value={freq}
          data-anchor={"billing-frequency-option"}
          data-testid={"billing-frequency-option"}
        >
          {upCaseString(contractBillingFreqToString[freq].toLowerCase())}
        </MenuItem>
      );
    });
  };

  const tierDisplayName =
    subscriptionTierDisplayMapping[subscription?.tier] || "Plan";

  return (
    <>
      <SubscriptionAction title={"Reactivate plan"}>
        {showLoadingSpinner ? (
          <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
            <CircularProgress
              color="primary"
              variant="indeterminate"
              thickness={4}
              size={30}
            />
          </Grid>
        ) : (
          <Button
            sx={{ mt: 1, mr: 2 }}
            data-testid="reactivate-plan-btn"
            variant="contained"
            color="primary"
            fullWidth
            disabled={isReactivateBtnDisabled()}
            onClick={() => setisDialogueOpen(true)}
          >
            Reactivate
          </Button>
        )}
      </SubscriptionAction>

      <ConfirmDialog
        title={`Reactivate ${tierDisplayName}`}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        width="md"
        open={isDialogueOpen}
        setOpen={setisDialogueOpen}
        onConfirm={() => {
          setisDialogueOpen(false);
          reactivateSubscription();
        }}
        disabled={isSubmitDisabled}
        onCancel={() => {
          cancelChanges();
        }}
      >
        <Grid container spacing={1}>
          {displayAlertBanner()}
          {isDecoupledEndOfTrial && (
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Plan Type</InputLabel>
                <Select
                  id="reactivate-plan-type-select"
                  data-anchor="reactivate-plan-type-select"
                  data-testid="reactivate-plan-type-select"
                  inputProps={{
                    "data-testid": "reactivate-plan-type-select-value",
                  }}
                  fullWidth
                  label="Plan Type"
                  required
                  value={updatedPlanType}
                  onChange={(event) => {
                    setUpdatedPlanType(
                      event.target.value as
                        | BillingFrequency.BILLING_FREQUENCY_MONTHLY
                        | BillingFrequency.BILLING_FREQUENCY_ANNUAL,
                    );
                  }}
                >
                  {displayBillingFrequencyOptions()}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body1">Billing Contact</Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="firstName"
              label="First name"
              inputProps={{
                "data-testid": "reactivate-plan-first-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setFirstName(e.target.value)}
              value={firstName}
              error={!firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="lastName"
              label="Last name"
              inputProps={{
                "data-testid": "reactivate-plan-last-name-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setLastName(e.target.value)}
              value={lastName}
              error={!lastName}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              required
              name="email"
              label="Email address"
              inputProps={{
                "data-testid": "reactivate-plan-org-email-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setContactEmail(e.target.value)}
              defaultValue={contactEmail}
              error={validateEmail(contactEmail) !== undefined}
            />
          </Grid>
          <Grid item xs={6}>
            {/* TODO: https://safetyculture.atlassian.net/browse/BSA-2525
            Replace generic text field with a phone input component.*/}
            <TextField
              name="phone"
              label="Phone number"
              inputProps={{
                "data-testid": "reactivate-plan-org-phone-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setContactNumber(e.target.value)}
              value={contactNumber}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">Shipping Contact</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSameShippingContact}
                  onChange={(_event, v) => {
                    onSameShippingContactChange(v);
                  }}
                  data-testid="reactivate-plan-same-shipping-contact-checkbox"
                />
              }
              label="Same as billing contact"
              labelPlacement="end"
            />
          </Grid>
          {!isSameShippingContact && (
            <>
              <Grid item xs={6}>
                <TextField
                  name="firstName"
                  label="First name"
                  inputProps={{
                    "data-testid": "reactivate-plan-shipping-first-name-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => handleShippingContactChange(e, "firstName")}
                  value={shippingContact.firstName}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="lastName"
                  label="Last name"
                  inputProps={{
                    "data-testid": "reactivate-plan-shipping-last-name-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => handleShippingContactChange(e, "lastName")}
                  value={shippingContact.lastName}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="email"
                  label="Email address"
                  inputProps={{
                    "data-testid": "reactivate-plan-shipping-email-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) => handleShippingContactChange(e, "email")}
                  value={shippingContact.email}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name="phone"
                  label="Phone number"
                  inputProps={{
                    "data-testid": "reactivate-plan-shipping-phone-field",
                  }}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  onChange={(e) =>
                    handleShippingContactChange(e, "phoneNumber")
                  }
                  value={shippingContact.phoneNumber}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Typography variant="body1">Shipping Address</Typography>
          </Grid>
          <AddressForm
            address={shippingAddress}
            onAddressChange={onShippingAddressChange}
            countryCodeFilter={["RU"]}
          />
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={useShippingAsBilling}
                  onChange={(_event, v) => {
                    onSameBillingChange(v);
                  }}
                  data-testid="reactivate-plan-use-shipping-as-billing"
                />
              }
              label={"Use Shipping address as Billing address"}
              labelPlacement="end"
            />
          </Grid>
          {!useShippingAsBilling && (
            <>
              <Grid item xs={12}>
                <Typography variant="body1">Billing Address</Typography>
              </Grid>
              <AddressForm
                data-testid="reactivate-plan-billing-address-form"
                address={billingAddress}
                onAddressChange={onBillingAddressChange}
                countryCodeFilter={["RU"]}
              />
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeTrial}
                onChange={(_event, v) => {
                  setIncludeTrial(v);
                }}
                data-testid="reactivate-plan-include-trial-checkbox"
              />
            }
            label={"Include 30 days trial"}
            labelPlacement="end"
          />
        </Grid>
        {canApplyCoupon && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isApplyingCoupon}
                  onChange={(_event, v) => {
                    handleApplyCoupon(v);
                  }}
                  data-testid="reactivate-plan-apply-coupon-checkbox"
                />
              }
              label={"Apply coupon"}
              labelPlacement="end"
            />
          </Grid>
        )}
        {canApplyCoupon && isApplyingCoupon && (
          <Grid item xs={12}>
            <TextField
              required
              name="couponCode"
              label="Coupon code"
              inputProps={{
                "data-testid": "reactivate-plan-apply-coupon-field",
              }}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => validateCoupon(e.target.value)}
              defaultValue={couponCode}
              error={isApplyingCoupon && !couponCode}
            />
          </Grid>
        )}
        {infoPanel()}
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
      >
        <Alert severity="success" variant="filled">
          Successfully reactivated plan
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReactivateSubscriptionZuora;
